<template>
    <div class="tiptap-wrap" ref="wrapEl" :class="{ 'tiptap-wrap--popup-open': mentionShow }">
        <!-- Toolbar -->
        <div v-if="editor" class="tiptap-toolbar">
            <!-- Heading -->
            <select class="tiptap-heading-select" @change="setHeading" :value="activeHeading">
                <option value="0">Paragraph</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
            </select>

            <span class="tiptap-sep"></span>

            <!-- Text formatting -->
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('bold') }"
                    @click="editor.chain().focus().toggleBold().run()" title="Bold">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 15.5H10v-3h3.5a1.5 1.5 0 0 1 0 3zm-3.5-9h3a1.5 1.5 0 0 1 0 3H10V6.5zM5 19h8.5c2.5 0 4.5-2 4.5-4.5 0-1.5-.7-2.8-1.8-3.6A3.5 3.5 0 0 0 13 5H5v14z"/></svg>
            </button>
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('italic') }"
                    @click="editor.chain().focus().toggleItalic().run()" title="Italic">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 5v3h2.2l-3.4 8H6v3h8v-3h-2.2l3.4-8H18V5z"/></svg>
            </button>
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('underline') }"
                    @click="editor.chain().focus().toggleUnderline().run()" title="Underline">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 21h14v-2H5v2zM12 17c3.3 0 6-2.7 6-6V3h-2.5v8c0 1.9-1.6 3.5-3.5 3.5S8.5 12.9 8.5 11V3H6v8c0 3.3 2.7 6 6 6z"/></svg>
            </button>
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('link') }"
                    @click="setLink" title="Link">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.7 1.4-3.1 3.1-3.1h4V7H7C4.2 7 2 9.2 2 12s2.2 5 5 5h4v-1.9H7c-1.7 0-3.1-1.4-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.7 0 3.1 1.4 3.1 3.1s-1.4 3.1-3.1 3.1h-4V17h4c2.8 0 5-2.2 5-5s-2.2-5-5-5z"/></svg>
            </button>

            <span class="tiptap-sep"></span>

            <!-- Lists -->
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('bulletList') }"
                    @click="editor.chain().focus().toggleBulletList().run()" title="Bullet List">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm0-6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm0 12a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg>
            </button>
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('orderedList') }"
                    @click="editor.chain().focus().toggleOrderedList().run()" title="Ordered List">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-7v2h14V4H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/></svg>
            </button>
            <button type="button" class="tiptap-btn"
                    @click="editor.chain().focus().liftListItem('listItem').run()"
                    :disabled="!editor.can().liftListItem('listItem')" title="Outdent">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 17h10v-2H11v2zm-8-5 4 4V8l-4 4zm0 9h18v-2H3v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/></svg>
            </button>
            <button type="button" class="tiptap-btn"
                    @click="editor.chain().focus().sinkListItem('listItem').run()"
                    :disabled="!editor.can().sinkListItem('listItem')" title="Indent">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zM3 8v8l4-4-4-4zm8 9h10v-2H11v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/></svg>
            </button>

            <span class="tiptap-sep"></span>

            <!-- Table -->
            <div class="tiptap-dropdown-wrap" ref="tableDropWrap">
                <button type="button" class="tiptap-btn tiptap-btn--caret"
                        :class="{ 'is-active': editor.isActive('table') }"
                        @click="tableMenuOpen = !tableMenuOpen" title="Table">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v18H3V3zm8 14h6v-4h-6v4zm0-6h6V7h-6v4zM5 17h4v-4H5v4zm0-6h4V7H5v4z"/></svg>
                    <svg class="tiptap-caret" viewBox="0 0 10 6" fill="currentColor"><path d="M0 0l5 6 5-6z"/></svg>
                </button>
                <div v-if="tableMenuOpen" class="tiptap-dropdown-menu" v-click-outside="() => tableMenuOpen = false">
                    <button type="button" @click="insertTable">Insert Table</button>
                    <template v-if="editor.isActive('table')">
                        <div class="tiptap-dropdown-divider"></div>
                        <button type="button" @click="editor.chain().focus().addRowBefore().run()">Add row above</button>
                        <button type="button" @click="editor.chain().focus().addRowAfter().run()">Add row below</button>
                        <button type="button" @click="editor.chain().focus().addColumnBefore().run()">Add column left</button>
                        <button type="button" @click="editor.chain().focus().addColumnAfter().run()">Add column right</button>
                        <div class="tiptap-dropdown-divider"></div>
                        <button type="button" @click="editor.chain().focus().deleteRow().run()">Delete row</button>
                        <button type="button" @click="editor.chain().focus().deleteColumn().run()">Delete column</button>
                        <button type="button" class="danger" @click="editor.chain().focus().deleteTable().run()">Delete table</button>
                    </template>
                </div>
            </div>

            <!-- Blockquote -->
            <button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('blockquote') }"
                    @click="editor.chain().focus().toggleBlockquote().run()" title="Blockquote">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 17h3l2-4V7H5v6h3l-2 4zm8 0h3l2-4V7h-6v6h3l-2 4z"/></svg>
            </button>

            <span class="tiptap-sep"></span>

            <!-- Image -->
            <button type="button" class="tiptap-btn" @click="triggerImageUpload" title="Upload Image">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
            </button>
            <input ref="imageInput" type="file" accept="image/*" style="display:none" @change="uploadImage" />

            <span class="tiptap-sep"></span>

            <!-- Undo / Redo -->
            <button type="button" class="tiptap-btn"
                    @click="editor.chain().focus().undo().run()"
                    :disabled="!editor.can().undo()" title="Undo">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>
            </button>
            <button type="button" class="tiptap-btn"
                    @click="editor.chain().focus().redo().run()"
                    :disabled="!editor.can().redo()" title="Redo">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>
            </button>
        </div>

        <!-- Editor Content -->
        <EditorContent :editor="editor" class="tiptap-content" />

        <!-- Mention dropdown -->
        <div v-if="mentionShow && mentionItems.length"
             class="tiptap-mention-popup"
             :class="{ 'tiptap-mention-popup--dark': isDarkMode }"
             :style="{ top: mentionTop + 'px', left: mentionLeft + 'px' }">
            <div v-for="(item, i) in mentionItems" :key="item.id"
                 :class="['tiptap-mention-item', { 'is-selected': i === mentionSelectedIndex }]"
                 @mousedown.prevent="selectMention(item)"
                 @mouseenter="mentionSelectedIndex = i">
                <img v-if="item.photo" :src="item.photo" class="tiptap-mention-avatar" @error="e => e.target.style.display='none'" />
                <span v-else class="tiptap-mention-avatar tiptap-mention-initials">{{ initials(item.label) }}</span>
                <div class="tiptap-mention-info">
                    <span class="tiptap-mention-name">{{ item.label }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import Mention from '@tiptap/extension-mention'
import { Plugin } from 'prosemirror-state'

function uploadImageToEditor(view, file) {
    const formData = new FormData()
    formData.append('upload', file)
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    fetch('/cke/image', {
        method: 'POST',
        headers: { 'X-CSRF-TOKEN': token ?? '' },
        body: formData,
    })
    .then(res => res.json())
    .then(data => {
        if (data.url) {
            const { state, dispatch } = view
            const node = state.schema.nodes.image.create({ src: data.url })
            dispatch(state.tr.replaceSelectionWith(node))
        }
    })
    .catch(() => {})
}

const PasteImage = Extension.create({
    name: 'pasteImage',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handlePaste(view, event) {
                        const items = Array.from(event.clipboardData?.items ?? [])
                        const imageItem = items.find(i => i.type.startsWith('image/'))
                        if (!imageItem) return false

                        event.preventDefault()
                        const file = imageItem.getAsFile()
                        if (!file) return false

                        uploadImageToEditor(view, file)
                        return true
                    },
                    handleDrop(view, event) {
                        const files = Array.from(event.dataTransfer?.files ?? [])
                        const imageFile = files.find(f => f.type.startsWith('image/'))
                        if (!imageFile) return false

                        event.preventDefault()
                        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
                        if (coords) {
                            const tr = view.state.tr.setSelection(
                                view.state.selection.constructor.near(view.state.doc.resolve(coords.pos))
                            )
                            view.dispatch(tr)
                        }
                        uploadImageToEditor(view, imageFile)
                        return true
                    },
                },
            }),
        ]
    },
})

const isDarkMode = computed(() => !!document.querySelector('.dark, [data-mode="dark"]'))

const props = defineProps({
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: 'Tulis sesuatu...' },
    mention: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

// Toolbar state
const tableMenuOpen = ref(false)
const imageInput = ref(null)

// Mention state
const mentionShow = ref(false)
const mentionItems = ref([])
const mentionSelectedIndex = ref(0)
const mentionTop = ref(0)
const mentionLeft = ref(0)
const wrapEl = ref(null)
let mentionCommandFn = null
let mentionRange = null

function handleDocumentMousedown(e) {
    if (!mentionShow.value || !mentionItems.value.length) return
    if (wrapEl.value && !wrapEl.value.contains(e.target)) {
        mentionShow.value = false
        mentionItems.value = []
    }
}

// Position the popup relative to the editor wrapper instead of the viewport.
// CSS `zoom` on <html> (used for desktop layout) shifts the coordinate space of
// getBoundingClientRect() relative to position:fixed elements teleported to <body>,
// but offsets between two rects measured in the same pass stay correct regardless of zoom.
function updateMentionPosition(clientRect) {
    const rect = clientRect?.()
    if (!rect) return
    const wrapRect = wrapEl.value?.getBoundingClientRect()
    if (!wrapRect) return
    // Absolute positioning relative to tiptap-wrap — zoom-invariant
    mentionTop.value = rect.bottom - wrapRect.top + 4
    mentionLeft.value = rect.left - wrapRect.left
}

function buildExtensions() {
    const exts = [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Underline,
        Link.configure({ openOnClick: false }),
        Image,
        PasteImage,
        Table.configure({ resizable: false }),
        TableRow,
        TableCell,
        TableHeader,
        Placeholder.configure({ placeholder: props.placeholder }),
    ]

    if (props.mention) {
        exts.push(Mention.configure({
            HTMLAttributes: { class: 'mention' },
            renderHTML({ options, node }) {
                return ['span', { 'data-type': 'mention', 'data-id': node.attrs.id, 'data-label': node.attrs.label, class: 'mention' }, `@${node.attrs.label}`]
            },
            suggestion: {
                char: '@',
                allowSpaces: false,
                minLength: 0,
                items: async ({ query }) => {
                    if ((query ?? '').includes('.')) return []
                    try {
                        const url = window.route
                            ? window.route('users.mention-search') + '?q=' + encodeURIComponent(query ?? '')
                            : '/dashboard/api/users/mention-search?q=' + encodeURIComponent(query ?? '')
                        const res = await fetch(url)
                        if (!res.ok) return []
                        return await res.json()
                    } catch { return [] }
                },
                render: () => ({
                    onStart(p) {
                        mentionRange = p.range
                        mentionCommandFn = p.command
                        mentionItems.value = p.items
                        mentionSelectedIndex.value = 0
                        updateMentionPosition(p.clientRect)
                        mentionShow.value = true
                    },
                    onUpdate(p) {
                        mentionRange = p.range
                        mentionCommandFn = p.command
                        mentionItems.value = p.items
                        updateMentionPosition(p.clientRect)
                    },
                    onKeyDown({ event }) {
                        if (event.key === 'Escape') {
                            if (mentionRange && editor.value) {
                                editor.value.chain().focus().deleteRange(mentionRange).run()
                            }
                            mentionShow.value = false
                            mentionItems.value = []
                            mentionRange = null
                            return true
                        }
                        if (!mentionShow.value || !mentionItems.value.length) return false
                        if (event.key === 'ArrowUp') {
                            mentionSelectedIndex.value = Math.max(0, mentionSelectedIndex.value - 1)
                            return true
                        }
                        if (event.key === 'ArrowDown') {
                            mentionSelectedIndex.value = Math.min(mentionItems.value.length - 1, mentionSelectedIndex.value + 1)
                            return true
                        }
                        if (event.key === 'Enter') {
                            const item = mentionItems.value[mentionSelectedIndex.value]
                            if (item) selectMention(item)
                            return true
                        }
                        return false
                    },
                    onExit() {
                        mentionRange = null
                        mentionShow.value = false
                        mentionItems.value = []
                    },
                }),
            },
        }))
    }
    return exts
}

const editor = useEditor({
    content: props.modelValue,
    extensions: buildExtensions(),
    onUpdate({ editor }) {
        emit('update:modelValue', editor.getHTML())
    },
})

watch(() => props.modelValue, (val) => {
    if (editor.value && editor.value.getHTML() !== val) {
        editor.value.commands.setContent(val ?? '', false)
    }
})

const activeHeading = computed(() => {
    if (!editor.value) return '0'
    for (const l of [1, 2, 3]) {
        if (editor.value.isActive('heading', { level: l })) return String(l)
    }
    return '0'
})

function setHeading(e) {
    const level = parseInt(e.target.value)
    if (!editor.value) return
    if (level === 0) editor.value.chain().focus().setParagraph().run()
    else editor.value.chain().focus().toggleHeading({ level }).run()
}

function setLink() {
    const prev = editor.value?.getAttributes('link').href ?? ''
    const url = window.prompt('URL:', prev)
    if (url === null) return
    if (url === '') {
        editor.value.chain().focus().unsetLink().run()
        return
    }
    editor.value.chain().focus().setLink({ href: url, target: '_blank' }).run()
}

function insertTable() {
    editor.value?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    tableMenuOpen.value = false
}

function triggerImageUpload() {
    imageInput.value?.click()
}

async function uploadImage(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const view = editor.value?.view
    if (view) uploadImageToEditor(view, file)
    e.target.value = ''
}

function selectMention(item) {
    if (mentionCommandFn) mentionCommandFn({ id: item.id, label: item.label })
    mentionShow.value = false
}

function initials(name) {
    return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() ?? '?'
}

// Click-outside directive for table dropdown
const vClickOutside = {
    mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e) }
        document.addEventListener('mousedown', el._clickOutside)
    },
    unmounted(el) {
        document.removeEventListener('mousedown', el._clickOutside)
    },
}

onMounted(() => {
    document.addEventListener('mousedown', handleDocumentMousedown)
})

onBeforeUnmount(() => {
    editor.value?.destroy()
    document.removeEventListener('mousedown', handleDocumentMousedown)
})
</script>
